import Splide from '@theme/splide';

class ProductMediaGallery extends HTMLElement {
  #controller = null;
  #mainSplide = null;
  #thumbsSplide = null;

  connectedCallback() {
    const mainTrack = this.querySelector('[data-splide-track]');
    if (!mainTrack) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const thumbnailSizeMobile = Number(this.dataset.thumbnailSizeMobile) || 80;
    const thumbnailSizeDesktop = Number(this.dataset.thumbnailSizeDesktop) || 80;

    this.#mainSplide = new Splide(mainTrack, {
      gap: 20,
      type: 'slide',
      rewind: false,
      pagination: false,
      arrows: false,
      speed: 400,
    });

    const thumbsTrack = this.querySelector('[data-splide-thumbs]');
    if (thumbsTrack) {
      this.#thumbsSplide = new Splide(thumbsTrack, {
        fixedWidth: thumbnailSizeDesktop,
        fixedHeight: thumbnailSizeDesktop,
        gap: 8,
        rewind: false,
        pagination: false,
        arrows: false,
        isNavigation: true,
        focus: 'left',
        breakpoints: {
          749: {
            fixedWidth: thumbnailSizeMobile,
            fixedHeight: thumbnailSizeMobile,
            gap: 8,
          },
        },
      });

      this.#mainSplide.sync(this.#thumbsSplide);
    }

    const onSlideChange = (newIndex) => this.#syncSlideUi(newIndex);

    this.#mainSplide.on('moved', onSlideChange);
    this.#thumbsSplide?.on('moved', onSlideChange);

    this.#mainSplide.mount();
    this.#thumbsSplide?.mount();

    this.querySelectorAll('[data-splide-prev]').forEach((button) => {
      button.addEventListener('click', () => this.#mainSplide?.go('<'), { signal });
    });
    this.querySelectorAll('[data-splide-next]').forEach((button) => {
      button.addEventListener('click', () => this.#mainSplide?.go('>'), { signal });
    });
    this.#syncSlideUi();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#mainSplide?.off('moved');
    this.#thumbsSplide?.off('moved');
    this.#mainSplide?.destroy();
    this.#thumbsSplide?.destroy();
    this.#mainSplide = null;
    this.#thumbsSplide = null;
  }

  #syncSlideUi(newIndex) {
    const index = typeof newIndex === 'number' ? newIndex : (this.#mainSplide?.index ?? 0);
    this.#updateStatus(index);
    this.#updateArrows(index);
    this.#updateThumbFades(index);
  }

  #updateArrows(index) {
    const total = this.#mainSplide?.length ?? 0;
    const isPreviousDisabled = index <= 0;
    const isNextDisabled = index >= total - 1;

    this.querySelectorAll('[data-splide-prev]').forEach((button) => {
      button.disabled = isPreviousDisabled;
      button.setAttribute('aria-disabled', isPreviousDisabled ? 'true' : 'false');
    });

    this.querySelectorAll('[data-splide-next]').forEach((button) => {
      button.disabled = isNextDisabled;
      button.setAttribute('aria-disabled', isNextDisabled ? 'true' : 'false');
    });
  }

  #updateThumbFades(index) {
    const thumbsTrack = this.querySelector('[data-splide-thumbs]');
    if (!thumbsTrack) return;

    const total = this.#mainSplide?.length ?? 0;

    thumbsTrack.classList.toggle('product-gallery__thumbs-splide--at-start', index <= 0);
    thumbsTrack.classList.toggle('product-gallery__thumbs-splide--at-end', index >= total - 1);
  }

  #updateStatus(index) {
    const status = this.querySelector('[data-gallery-status]');
    const total = this.#mainSplide?.length ?? 0;

    if (status && total > 0) {
      const template = this.dataset.statusTemplate || '';
      status.textContent = template.replace('[current]', String(index + 1)).replace('[total]', String(total));
    }
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
